// @LTG-ID: F-090
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md, 08_FEATURES.md#F-090,
//        ORCH-0048 P3-M2 (BriefPage shell), ORCH-0050 A3 (generic shell),
//        CHG-0102 (Journal MVP body/FAQ/SEO), CHG-0105 (article-page polish)
// @CHANGED: 2026-05-29 session_007 (CHG-0105 · left share rail + right TOC
//           rail + reading progress + action row + ID stamp; print stylesheet)
// @OWNER: web
//
// F-080 — `<BriefPage>` generic shell for every editorial brief in The KGOB
// Journals. Renders the article scaffolding (breadcrumb, title, deck, byline,
// At-a-Glance, body slot, end CTA, sources, read-next rail) and injects the
// per-brief JSON-LD `Article` / `BlogPosting` schema via react-helmet-async.
//
// CHG-0105 additions (all additive, backward-compatible):
//   - ReadingProgress bar (fixed top, tan fill, appears after 200px scroll)
//   - ShareRail (LinkedIn / X / Copy / Email) — sticky left column ≥1024px
//   - TocRail — auto-extracted from H2s; sticky right column ≥1024px;
//                IntersectionObserver active-section highlighting;
//                Sources shortcut card
//   - ActionRow (TL;DR anchor + PRINT/PDF + ID stamp) between byline & body
//   - Print stylesheet in brief-page.css hides rails + chrome
//
// TOC extraction paths (dual mode):
//   - v2 markdown briefs — parse H2s from the `bodyMarkdown` string using
//     the same github-slugger algo that rehype-slug uses at render time,
//     so ids match exactly.
//   - legacy JSX briefs (Brief 01 / 02) — walk the rendered DOM under
//     `.ltg-brief-body` after mount; assign missing ids on the fly.
//
// Hard rules carried over:
//   - Every app.letstalkgrowth.com outbound goes through <ExternalCTA>. No bare anchors.
//   - The body inherits Source Serif 4 + gold accent ONLY when wrapped in
//     `.ltg-brief-page`; this restricts the editorial palette to /journals/*.

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import GithubSlugger from "github-slugger";
import ExternalCTA from "../../lib/external-cta";
import "./brief-page.css";

// CHG-0102 · Canonical origin used to promote a relative ogImage path
// (`/journals/og/_default.jpg`) into an absolute URL for og:image + Twitter
// Card. Hardcoded to match FRONTMATTER.canonical roots so a preview
// deployment does not accidentally emit meta pointing at localhost.
const CANONICAL_ROOT = "https://letstalkgrowth.com";

// CHG-0102 · Build the BlogPosting + FAQPage + BreadcrumbList @graph payload
// per v2 dispatch Task 2b. FAQPage is included only when the article ships
// at least one FAQ pair.
function buildJsonLdGraph(fm, faq) {
    const ogAbs = fm.ogImage && fm.ogImage.startsWith("http")
        ? fm.ogImage
        : `${CANONICAL_ROOT}${fm.ogImage || "/journals/og/_default.jpg"}`;
    const graph = [
        {
            "@type": "BlogPosting",
            "@id": `${fm.canonical}#post`,
            headline: fm.title,
            description: fm.deck,
            datePublished: fm.publishedISO,
            dateModified: fm.lastReviewed || fm.publishedISO,
            author: {
                "@type": "Person",
                name: fm.author,
                jobTitle: fm.authorRole,
                worksFor: {
                    "@type": "AccountingService",
                    name: "KGOB CPAs & Advisors",
                    url: "https://letstalkgrowth.com",
                },
            },
            publisher: {
                "@type": "Organization",
                name: "Let\u2019s Talk Growth",
                url: "https://letstalkgrowth.com",
                logo: {
                    "@type": "ImageObject",
                    url: "https://letstalkgrowth.com/logo/ltg-logo.png",
                    width: 600,
                    height: 60,
                },
            },
            image: {
                "@type": "ImageObject",
                url: ogAbs,
                width: 1200,
                height: 630,
            },
            url: fm.canonical,
            keywords: fm.keywords,
            articleSection: fm.articleSection,
            wordCount: fm.wordCount,
            timeRequired: fm.timeRequired,
            inLanguage: "en-US",
            isPartOf: {
                "@type": "Blog",
                "@id": "https://letstalkgrowth.com/journals#blog",
                name: "Let\u2019s Talk Growth \u00b7 The Journal",
            },
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": fm.canonical,
            },
            articleBody: fm.articleBodyPreview,
        },
    ];
    if (faq && faq.length > 0) {
        graph.push({
            "@type": "FAQPage",
            "@id": `${fm.canonical}#faq`,
            mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
        });
    }
    graph.push({
        "@type": "BreadcrumbList",
        "@id": `${fm.canonical}#breadcrumb`,
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${CANONICAL_ROOT}/` },
            { "@type": "ListItem", position: 2, name: "The Journal", item: `${CANONICAL_ROOT}/journals` },
            { "@type": "ListItem", position: 3, name: fm.articleSection, item: `${CANONICAL_ROOT}/journals#${fm.sectionAnchor}` },
            { "@type": "ListItem", position: 4, name: fm.title, item: fm.canonical },
        ],
    });
    return { "@context": "https://schema.org", "@graph": graph };
}

// -----------------------------------------------------------------------
// CHG-0105 · TOC extraction helpers.
// -----------------------------------------------------------------------

// Extract section headings from raw markdown for the right-rail TOC.
// The CHG-0102 corpus is inconsistent about which heading level it uses
// for top-level sections — some briefs use `## `, others use `#### ` — so
// we scan H2..H4 and keep only the shallowest level actually present per
// article. This yields a single flat-level TOC per brief regardless of
// authoring convention. Slugs match rehype-slug's github-slugger output
// so ids align 1:1 with what react-markdown assigns at render time.
function extractTocFromMarkdown(md) {
    if (!md) return [];
    const slugger = new GithubSlugger();
    const raw = [];
    let inFence = false;
    for (const line of md.split("\n")) {
        if (line.startsWith("```")) {
            inFence = !inFence;
            continue;
        }
        if (inFence) continue;
        const m = line.match(/^(#{2,4})\s+(.+?)\s*#*\s*$/);
        if (m) {
            const level = m[1].length;
            const text = m[2]
                .replace(/\*\*([^*]+)\*\*/g, "$1")
                .replace(/__([^_]+)__/g, "$1")
                .replace(/`([^`]+)`/g, "$1")
                .trim();
            if (text) raw.push({ level, text });
        }
    }
    if (!raw.length) return [];
    const minLevel = Math.min(...raw.map((r) => r.level));
    return raw
        .filter((r) => r.level === minLevel)
        .map((r) => ({ id: slugger.slug(r.text), text: r.text }));
}

// Legacy JSX briefs — walk rendered DOM after mount, assign missing ids.
// Same shallowest-level-present rule as the markdown extractor.
function extractTocFromDom(root) {
    if (!root) return [];
    const nodes = Array.from(root.querySelectorAll("h2, h3, h4"));
    if (!nodes.length) return [];
    const slugger = new GithubSlugger();
    const levelOf = (el) => parseInt(el.tagName.slice(1), 10);
    const minLevel = Math.min(...nodes.map(levelOf));
    const items = [];
    nodes
        .filter((el) => levelOf(el) === minLevel)
        .forEach((el) => {
            const text = (el.textContent || "").trim();
            if (!text) return;
            if (!el.id) el.id = slugger.slug(text);
            items.push({ id: el.id, text });
        });
    return items;
}

// -----------------------------------------------------------------------
// CHG-0105 · Reading-progress bar.
// -----------------------------------------------------------------------

const ReadingProgress = () => {
    const [pct, setPct] = useState(0);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            const h =
                document.documentElement.scrollHeight - window.innerHeight;
            const p = h > 0 ? Math.min(100, Math.max(0, (y / h) * 100)) : 0;
            setPct(p);
            setVisible(y > 200);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, []);
    return (
        <div
            className={`ltg-reading-progress${visible ? " is-visible" : ""}`}
            data-testid="brief-reading-progress"
            aria-hidden="true"
        >
            <div
                className="ltg-reading-progress__fill"
                style={{ width: `${pct}%` }}
            />
        </div>
    );
};

// -----------------------------------------------------------------------
// CHG-0105 · Share rail (LinkedIn / X / Copy / Email).
// -----------------------------------------------------------------------

const ShareRail = ({ title, canonical, onCopy }) => {
    const url = canonical || (typeof window !== "undefined" ? window.location.href : "");
    const t = encodeURIComponent(title || "");
    const u = encodeURIComponent(url);
    const openPopup = (href) =>
        window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
    const copyLink = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const ta = document.createElement("textarea");
                ta.value = url;
                ta.setAttribute("readonly", "");
                ta.style.position = "absolute";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            if (onCopy) onCopy();
        } catch {
            /* clipboard blocked — silent fail */
        }
    };
    return (
        <aside
            className="ltg-share-rail"
            data-testid="brief-share-rail"
            aria-label="Share this essay"
        >
            <button
                type="button"
                className="ltg-share-btn"
                aria-label="Share on LinkedIn"
                data-testid="brief-share-linkedin"
                onClick={() =>
                    openPopup(
                        `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
                    )
                }
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path d="M20.5 3h-17A.5.5 0 003 3.5v17a.5.5 0 00.5.5h17a.5.5 0 00.5-.5v-17a.5.5 0 00-.5-.5zM8.6 18.3H5.7V9.7h2.9v8.6zM7.2 8.4a1.7 1.7 0 110-3.4 1.7 1.7 0 010 3.4zM18.3 18.3h-2.9V14c0-1-.4-1.7-1.3-1.7-.7 0-1.1.5-1.3 1a1.6 1.6 0 00-.1.6v4.4H9.8V9.7h2.9v1.2c.4-.6 1.1-1.4 2.6-1.4 1.9 0 3 1.2 3 3.9v4.9z" />
                </svg>
            </button>
            <button
                type="button"
                className="ltg-share-btn"
                aria-label="Share on X"
                data-testid="brief-share-twitter"
                onClick={() =>
                    openPopup(
                        `https://twitter.com/intent/tweet?text=${t}&url=${u}&via=letstalkgrowth`,
                    )
                }
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path d="M17.5 3h3.2l-7 8 8.2 10h-6.4l-5-6.6-5.7 6.6H1.6l7.5-8.5L1.2 3H8l4.5 6 5-6zm-1.1 16.1h1.7L7.5 4.8H5.7l10.7 14.3z" />
                </svg>
            </button>
            <button
                type="button"
                className="ltg-share-btn"
                aria-label="Copy link"
                data-testid="brief-share-copy"
                onClick={copyLink}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1.5 1.5" />
                    <path d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1.5-1.5" />
                </svg>
            </button>
            <a
                className="ltg-share-btn"
                aria-label="Share by email"
                data-testid="brief-share-email"
                href={`mailto:?subject=${t}&body=${u}`}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 7l9 6 9-6" />
                </svg>
            </a>
        </aside>
    );
};

// -----------------------------------------------------------------------
// CHG-0105 · TOC rail with IntersectionObserver active-section highlight.
// -----------------------------------------------------------------------

const TocRail = ({ items, sourcesCount }) => {
    const [active, setActive] = useState(items[0]?.id || null);

    useEffect(() => {
        if (!items.length) return undefined;
        // Resolve DOM targets — may be null if the corresponding H2 has not
        // rendered yet; retry once via requestAnimationFrame.
        const resolve = () =>
            items.map((i) => document.getElementById(i.id)).filter(Boolean);
        let targets = resolve();
        if (!targets.length) {
            const raf = requestAnimationFrame(() => {
                targets = resolve();
            });
            return () => cancelAnimationFrame(raf);
        }
        const obs = new IntersectionObserver(
            (entries) => {
                const hit = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.target.offsetTop - b.target.offsetTop);
                if (hit.length > 0) setActive(hit[0].target.id);
            },
            { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
        );
        targets.forEach((t) => obs.observe(t));
        return () => obs.disconnect();
    }, [items]);

    if (!items.length && !sourcesCount) return null;

    return (
        <aside
            className="ltg-toc-rail"
            data-testid="brief-toc-rail"
            aria-label="Article outline"
        >
            {items.length ? (
                <details className="ltg-toc-details" open>
                    <summary
                        className="ltg-toc-summary"
                        data-testid="brief-toc-summary"
                    >
                        In this essay
                    </summary>
                    <div className="ltg-toc-card">
                        <p className="ltg-toc-card__label">IN THIS ESSAY</p>
                        <ol className="ltg-toc-list">
                            {items.map((it, i) => (
                                <li
                                    key={it.id}
                                    className={`ltg-toc-item${
                                        active === it.id ? " is-active" : ""
                                    }`}
                                >
                                    <a
                                        href={`#${it.id}`}
                                        data-testid={`brief-toc-item-${it.id}`}
                                    >
                                        <span className="ltg-toc-item__num">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="ltg-toc-item__text">
                                            {it.text}
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </div>
                </details>
            ) : null}
            {sourcesCount ? (
                <a
                    href="#sources"
                    className="ltg-toc-sources"
                    data-testid="brief-toc-sources"
                >
                    <span className="ltg-toc-sources__label">
                        SOURCES &middot; &para;{" "}
                    </span>
                    <span className="ltg-toc-sources__count">
                        {sourcesCount}
                    </span>
                </a>
            ) : null}
        </aside>
    );
};

// -----------------------------------------------------------------------
// CHG-0105 · Action row (TL;DR / PRINT / ID stamp).
// -----------------------------------------------------------------------

const ActionRow = ({ slug }) => {
    const scrollToDeck = () => {
        const el = document.getElementById("deck");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const printPage = () => {
        if (typeof window !== "undefined") window.print();
    };
    return (
        <div className="ltg-action-row" data-testid="brief-action-row">
            <div className="ltg-action-row__left">
                <button
                    type="button"
                    className="ltg-action-btn"
                    onClick={scrollToDeck}
                    data-testid="brief-action-tldr"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                    TL;DR
                </button>
                <button
                    type="button"
                    className="ltg-action-btn"
                    onClick={printPage}
                    data-testid="brief-action-print"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="6 9 6 2 18 2 18 9" />
                        <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                        <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    PRINT / PDF
                </button>
            </div>
            <div
                className="ltg-action-row__id"
                data-testid="brief-action-id-stamp"
            >
                ID &middot; {String(slug || "").toUpperCase()}
            </div>
        </div>
    );
};

// -----------------------------------------------------------------------
// BriefPage — main shell.
// -----------------------------------------------------------------------

export const BriefPage = ({
    frontmatter,
    children,
    sources,
    endCta,
    readNext,
    // CHG-0102 v2 additions — backward-compatible optional props.
    bodyMarkdown,
    faq,
}) => {
    const {
        number,
        section,
        sectionHref = "/journals",
        sectionAnchor,
        title,
        deck,
        author = "Sara F. Gonzalez, CPA",
        authorRole = "Founder & Managing Partner",
        authorInitials = "SG",
        publishedHuman,
        publishedISO,
        readMinutes,
        lastReviewed,
        ctaId,
        ctaLabel = "Begin",
        jsonLd,
        pageTitle,
        canonical,
        // v2 fields — presence toggles the "modern" JSON-LD + meta mode.
        ogImage,
        keywords,
        articleSection,
        complianceNote,
        needsDisclaimerFooter,
    } = frontmatter;

    // Mode detection: v2 mode is active when the caller ships the enriched
    // frontmatter (articleSection is our sentinel). Legacy Brief 01 and
    // Brief 02 ship `jsonLd` and children instead; they keep their exact
    // existing render surface for backward compatibility.
    const isV2 = Boolean(articleSection);

    useEffect(() => {
        if (pageTitle) document.title = pageTitle;
    }, [pageTitle]);

    const jsonLdGraph = isV2 ? buildJsonLdGraph(frontmatter, faq) : null;
    const ogAbs = ogImage && ogImage.startsWith("http")
        ? ogImage
        : `${CANONICAL_ROOT}${ogImage || "/journals/og/_default.jpg"}`;
    const keywordTags = isV2 && keywords
        ? keywords.split(",").map((k) => k.trim()).filter(Boolean)
        : [];

    // ------------------------------------------------------------------
    // CHG-0105 state.
    // ------------------------------------------------------------------
    const bodyRef = useRef(null);
    const [domTocItems, setDomTocItems] = useState([]);
    const [copied, setCopied] = useState(false);

    // TOC from markdown source (deterministic — no DOM race).
    const markdownTocItems = useMemo(
        () => extractTocFromMarkdown(bodyMarkdown),
        [bodyMarkdown],
    );

    // For legacy briefs (no bodyMarkdown), extract TOC from rendered DOM.
    useEffect(() => {
        if (bodyMarkdown) return;
        // Defer to next tick so JSX children have committed to the DOM.
        const raf = requestAnimationFrame(() => {
            if (bodyRef.current) {
                setDomTocItems(extractTocFromDom(bodyRef.current));
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [bodyMarkdown, children]);

    const tocItems = bodyMarkdown ? markdownTocItems : domTocItems;

    const sourcesCount = sources && sources.length ? sources.length : 0;

    const slug = useMemo(() => {
        if (!canonical) return "brief";
        const parts = canonical.split("/").filter(Boolean);
        return parts[parts.length - 1] || "brief";
    }, [canonical]);

    // CHG-0110 Phase 4 · derive hero-image src from slug; fall back to the
    // shared default when slug is not yet resolvable (e.g. legacy briefs
    // without a canonical URL in dev preview). Phase 2 shipped 42 per-slug
    // hero renders under /journals-media/hero/, all 1600x900.
    const heroSrc = slug && slug !== "brief"
        ? `/journals-media/hero/${slug}.jpg`
        : "/journals-media/hero/_default.jpg";

    const onCopyLink = useCallback(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
    }, []);

    return (
        <div className="ltg-brief-page" data-testid="brief-page">
            {isV2 ? (
                <Helmet>
                    <title>{pageTitle}</title>
                    <meta name="description" content={deck} />
                    {canonical ? (
                        <link rel="canonical" href={canonical} />
                    ) : null}
                    <meta property="og:type" content="article" />
                    <meta
                        property="og:site_name"
                        content="Let&#39;s Talk Growth &middot; The Journal"
                    />
                    <meta property="og:title" content={title} />
                    <meta property="og:description" content={deck} />
                    <meta property="og:url" content={canonical} />
                    <meta property="og:image" content={ogAbs} />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="630" />
                    <meta property="article:author" content={author} />
                    <meta
                        property="article:published_time"
                        content={publishedISO}
                    />
                    <meta
                        property="article:modified_time"
                        content={lastReviewed || publishedISO}
                    />
                    <meta property="article:section" content={articleSection} />
                    {keywordTags.map((tag, i) => (
                        <meta key={i} property="article:tag" content={tag} />
                    ))}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={title} />
                    <meta name="twitter:description" content={deck} />
                    <meta name="twitter:image" content={ogAbs} />
                    <script
                        type="application/ld+json"
                        data-testid="brief-jsonld-graph"
                    >
                        {JSON.stringify(jsonLdGraph)}
                    </script>
                </Helmet>
            ) : jsonLd ? (
                <Helmet>
                    {canonical ? (
                        <link rel="canonical" href={canonical} />
                    ) : null}
                    <script
                        type="application/ld+json"
                        data-testid="brief-jsonld"
                    >
                        {JSON.stringify(jsonLd)}
                    </script>
                </Helmet>
            ) : null}

            {/* CHG-0105 · reading progress bar */}
            <ReadingProgress />

            <article>
                {/* CHG-0105 · 3-column grid wraps the article surface only.
                    End CTA + disclaimer + read-next continue at full width
                    below the grid, unchanged. */}
                <div className="ltg-brief-layout">
                    <ShareRail
                        title={title}
                        canonical={canonical}
                        onCopy={onCopyLink}
                    />

                    <div className="ltg-brief-container ltg-brief-layout__center">
                        <header
                            className="ltg-brief-article-header"
                            data-testid="brief-article-header"
                        >
                            <div
                                className="ltg-brief-breadcrumb"
                                data-testid="brief-breadcrumb"
                            >
                                <Link to="/journals">Journals</Link>
                                <span>/</span>
                                <Link
                                    to={
                                        sectionAnchor
                                            ? `${sectionHref}#${sectionAnchor}`
                                            : sectionHref
                                    }
                                >
                                    {section}
                                </Link>
                                <span>/</span>Brief {number}
                            </div>

                            {/* CHG-0110 Phase 4 · hero image slot (Surface B).
                                Placed between breadcrumb and title so the
                                editorial cover reads before the headline. */}
                            <img
                                src={heroSrc}
                                alt={title}
                                width="1600"
                                height="900"
                                loading="eager"
                                decoding="async"
                                className="ltg-brief-hero-image"
                                data-testid="brief-hero-image"
                            />

                            <h1
                                className="ltg-brief-title"
                                data-testid="brief-title"
                            >
                                {title}
                            </h1>

                            {/* CHG-0105 · id="deck" so the TL;DR button
                                can smooth-scroll here. */}
                            <p
                                id="deck"
                                className="ltg-brief-deck"
                                data-testid="brief-deck"
                            >
                                {deck}
                            </p>

                            <div
                                className="ltg-brief-byline"
                                data-testid="brief-byline"
                            >
                                <div className="ltg-brief-author">
                                    <span
                                        className="ltg-brief-author-avatar"
                                        aria-hidden="true"
                                    >
                                        {authorInitials}
                                    </span>
                                    <span>
                                        <strong>{author}</strong> &middot;{" "}
                                        {authorRole}
                                    </span>
                                </div>
                                {publishedISO && publishedHuman ? (
                                    <span>
                                        Published{" "}
                                        <time
                                            dateTime={publishedISO}
                                            data-testid="brief-published-time"
                                        >
                                            {publishedHuman}
                                        </time>
                                    </span>
                                ) : publishedHuman ? (
                                    <span>Published {publishedHuman}</span>
                                ) : null}
                                {readMinutes ? (
                                    <span>{readMinutes} min read</span>
                                ) : null}
                                {lastReviewed && !isV2 ? (
                                    <span>Last reviewed {lastReviewed}</span>
                                ) : null}
                            </div>
                        </header>

                        {/* CHG-0105 · action row (TL;DR + PRINT + ID stamp). */}
                        <ActionRow slug={slug} />

                        <section
                            ref={bodyRef}
                            className="ltg-brief-body"
                            data-testid="brief-body"
                        >
                            {bodyMarkdown ? (
                                <div
                                    className="ltg-brief-body__markdown"
                                    data-testid="brief-body-markdown"
                                >
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        rehypePlugins={[rehypeSlug]}
                                    >
                                        {bodyMarkdown}
                                    </ReactMarkdown>
                                </div>
                            ) : (
                                children
                            )}
                        </section>

                        {sources && sources.length > 0 ? (
                            <section
                                id="sources"
                                className="ltg-brief-sources"
                                data-testid="brief-sources"
                            >
                                <h3>Sources cited</h3>
                                <ol>
                                    {sources.map((src) => (
                                        <li
                                            key={src.id}
                                            dangerouslySetInnerHTML={{
                                                __html: src.html,
                                            }}
                                        />
                                    ))}
                                </ol>
                            </section>
                        ) : null}
                    </div>

                    <TocRail
                        items={tocItems}
                        sourcesCount={sourcesCount}
                    />
                </div>

                {endCta ? (
                    <div className="ltg-brief-container">
                        <div
                            className="ltg-brief-end-cta"
                            data-testid="brief-end-cta"
                        >
                            {endCta.label ? (
                                <span className="ltg-brief-end-cta-label">
                                    {endCta.label}
                                </span>
                            ) : null}
                            <h2>
                                {endCta.headlineLead}
                                {endCta.headlineEm ? (
                                    <em> {endCta.headlineEm}</em>
                                ) : null}
                                {endCta.headlineTrail || "."}
                            </h2>
                            {endCta.body ? <p>{endCta.body}</p> : null}
                            <div className="ltg-brief-btn-row">
                                <ExternalCTA
                                    ctaId={ctaId}
                                    className="ltg-brief-btn-primary"
                                    data-testid="brief-end-cta-primary"
                                >
                                    {ctaLabel}
                                </ExternalCTA>
                                {endCta.secondaryHref &&
                                endCta.secondaryLabel ? (
                                    <a
                                        href={endCta.secondaryHref}
                                        className="ltg-brief-btn-secondary"
                                        data-testid="brief-end-cta-secondary"
                                    >
                                        {endCta.secondaryLabel}
                                    </a>
                                ) : null}
                            </div>
                            {endCta.microLine ? (
                                <p className="ltg-brief-end-cta-micro">
                                    {endCta.microLine}
                                </p>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {isV2 && (needsDisclaimerFooter || complianceNote) ? (
                    <div className="ltg-brief-container">
                        <section
                            className="ltg-brief-disclaimer"
                            aria-label="Educational disclaimer"
                            data-testid="brief-disclaimer"
                        >
                            <p className="ltg-brief-disclaimer__title">
                                &mdash; EDUCATIONAL DISCLAIMER &mdash;
                            </p>
                            <p className="ltg-brief-disclaimer__body">
                                This article is educational and not
                                personalized professional advice. Statistics
                                are attributed to publicly available sources
                                and should be verified against the most
                                current publications. Consult your CPA or
                                attorney for decisions specific to your
                                business.
                            </p>
                        </section>
                    </div>
                ) : null}
            </article>

            {readNext && readNext.length > 0 ? (
                <section
                    className="ltg-brief-read-next"
                    data-testid="brief-read-next"
                >
                    <div className="ltg-brief-wide">
                        <span className="ltg-brief-read-next-label">
                            Read next
                        </span>
                        <h3>Continue the primer.</h3>
                        <div className="ltg-brief-read-next-grid">
                            {readNext.map((card, idx) => {
                                const inner = (
                                    <>
                                        <span className="ltg-brief-read-next-tag">
                                            {card.tag}
                                        </span>
                                        <h4>{card.title}</h4>
                                        <span className="ltg-brief-read-next-meta">
                                            {card.meta}
                                        </span>
                                    </>
                                );
                                return card.to.startsWith("/") ? (
                                    <Link
                                        key={idx}
                                        to={card.to}
                                        className="ltg-brief-read-next-card"
                                        data-testid={`brief-read-next-${idx}`}
                                    >
                                        {inner}
                                    </Link>
                                ) : (
                                    <a
                                        key={idx}
                                        href={card.to}
                                        className="ltg-brief-read-next-card"
                                        data-testid={`brief-read-next-${idx}`}
                                    >
                                        {inner}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* CHG-0105 · copy-link toast (2s auto-dismiss). */}
            {copied ? (
                <div
                    className="ltg-copy-toast"
                    role="status"
                    data-testid="brief-copy-toast"
                >
                    Link copied
                </div>
            ) : null}
        </div>
    );
};

export default BriefPage;
